/*
 * Purpose: Mission lifecycle — START required, GOAL usual, optional MapLock for dynamic maps
 * Last modified: 2026-06-27 | Agent
 * Completeness: 98
 */
import type { Workflow } from '@/stores/workflowStore';
import { PRODUCT_TERMS } from '@/lib/productTerms';

export function missionStartBlocks(workflow: Workflow | null | undefined) {
  return workflow?.nodes.filter((n) => n.type === 'start') ?? [];
}

export function missionGoalBlocks(workflow: Workflow | null | undefined) {
  return workflow?.nodes.filter((n) => n.type === 'goal' && n.subtype === 'outcome') ?? [];
}

export function isMapLocked(workflow: Workflow | null | undefined): boolean {
  return workflow?.missionMeta?.mapLocked === true;
}

/** When false (default), AI may replan the map mid-run unless MapLock is on */
export function allowsDynamicMap(workflow: Workflow | null | undefined): boolean {
  if (!workflow) return true;
  if (isMapLocked(workflow)) return false;
  return workflow.missionMeta?.allowDynamicMap !== false;
}

export function missionStartBlockedReason(workflow: Workflow | null | undefined): string | null {
  if (!workflow) return `Select or create a ${PRODUCT_TERMS.mission}.`;

  const starts = missionStartBlocks(workflow);
  if (starts.length === 0) {
    return `${PRODUCT_TERMS.mission} needs a START block — right-click the canvas to add one.`;
  }
  if (starts.length > 1) {
    return `${PRODUCT_TERMS.mission} has ${starts.length} START blocks — keep exactly one.`;
  }

  const start = starts[0];
  const hasOutgoing = workflow.edges.some((e) => e.from === start.id);
  if (!hasOutgoing && workflow.nodes.length > 1) {
    return `Connect the START block to your GOAL or first step before running.`;
  }

  return null;
}

export function missionValidationHints(workflow: Workflow | null | undefined): string[] {
  if (!workflow) return [];
  const hints: string[] = [];

  if (missionGoalBlocks(workflow).length === 0) {
    hints.push('Add a GOAL block with the identified outcome (recommended).');
  }

  if (allowsDynamicMap(workflow)) {
    hints.push('Map is unlocked — AI may adjust blocks between START and GOAL while running.');
  } else {
    hints.push('MapLock is on — the block map stays fixed during the run.');
  }

  return hints;
}
